import { memo, useCallback, useMemo } from "react"
import { NavigateFunction } from "react-router-dom"
import styled from "styled-components"
import clsx from "clsx"

import LogoSidebar from "./LogoSidebar"
import SidebarItem from "./components/SidebarItem"
import CollapsedLogoSidebar from "./CollapsedLogoSidebar"

const Container = styled.div`
  display: flex;
  background-color: ${(props) => props.theme.palette.primary_one};
  position: fixed;
  z-index: 997;
  top: 0;
  left: 0;
  height: 100vh;
  box-shadow: 0px 4px 47px rgba(0, 0, 0, 0.15);
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 24px 0;
  border-radius: 0px 40px 40px 0px;

  &.background-gradient {
    background: linear-gradient(251.11deg, #6729ea 29.56%, #002c98 77.48%);
  }

  img {
    margin-left: 24px;
  }
`

const MainOptions = styled.div`
  overflow-y: auto;
  margin-bottom: 48px;

  &::-webkit-scrollbar {
    width: 5px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.palette.sidebar_scrollbar};
    border-radius: 50px;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 50px;
    border: 1px solid ${({ theme }) => theme.palette.sidebar_scrollbar};
    background: ${({ theme }) => theme.palette.sidebar_scrollbar_thumb};
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.palette.sidebar_scrollbar_thumb_hover};
  }
`

const ActionOptions = styled.div`
  margin-top: auto;
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 280px;
  overflow-y: auto;

  &.collapsed {
    width: 84px;
  }
`

const CollapseIconWrapper = styled.div`
  position: absolute;
  top: 88px;
  right: -8px;
  cursor: pointer;

  & img {
    width: 24px;
    height: 24px;
    transform: rotate(180deg);
  }

  &.collapsed img {
    transform: rotate(0deg);
  }
`

interface SidebarProps extends App.SidebarProps {
  navigate: NavigateFunction
  collapsed: boolean
  setCollapsed: (collapsed: boolean) => void
}

function Sidebar(props: SidebarProps) {
  const { logoURL, options, actionOptions, navigate, className, collapsed, setCollapsed, isCollapsable } = props

  //   const { hasPermission } = useHasPermission();
  //   const dispatch = useAppDispatch();

  const handleItemClick = useCallback(
    (item) => {
      if (item.route) {
        //   dispatch(hideToast())
        navigate(`${item.route}`)

        return
      }

      if (collapsed) {
        setCollapsed(false)

        return
      }

      item.onClick?.()
    },
    [collapsed]
  )

  const hasPermissionOnChildren = useCallback((childrenOptions?: App.IItemOptions[]) => {
    let childrenLength: number | null = null

    if (childrenOptions) {
      const children = childrenOptions.filter((child) => {
        if (child.permission) {
          return false
        }

        return true
      })

      childrenLength = children.length
    }

    if (childrenOptions && childrenLength === 0) {
      return false
    }

    return true
  }, [])

  const handleToggleCollapse = useCallback(() => {
    setCollapsed(!collapsed)
  }, [collapsed])

  const parsedOptions = useMemo(() => {
    return options.filter((option) => {
      if (option.permission) {
        return false
      }

      if (!hasPermissionOnChildren(option.children)) {
        return false
      }

      return true
    })
  }, [options])

  const currentPathname = window.location.pathname

  return (
    <Container className={className}>
      {!collapsed && <LogoSidebar src={logoURL ?? ""} />}
      {collapsed && <CollapsedLogoSidebar src={""} />}
      {isCollapsable && (
        <CollapseIconWrapper className={clsx({ collapsed })} onClick={() => handleToggleCollapse()}>
          <img src={""} alt="arrow" />
        </CollapseIconWrapper>
      )}
      <Content className={clsx({ collapsed })}>
        <MainOptions>
          {parsedOptions.map((menu) => (
            <SidebarItem
              icon={menu.icon}
              title={menu.title}
              hasSubItems={!!menu.children?.length}
              onClick={() => handleItemClick(menu)}
              collapsed={collapsed}
              isActive={currentPathname === menu.route}
            >
              {menu.children?.map((subMenu) => (
                <SidebarItem
                  title={subMenu.title}
                  hideArrow={!subMenu.children?.length}
                  hasSubItems={!!subMenu.children?.length}
                  onClick={() => handleItemClick(subMenu)}
                  collapsed={collapsed}
                >
                  {subMenu.children?.map((subSubMenu) => (
                    <SidebarItem
                      title={subSubMenu.title}
                      hideArrow
                      onClick={() => handleItemClick(subSubMenu)}
                      collapsed={collapsed}
                    />
                  ))}
                </SidebarItem>
              ))}
            </SidebarItem>
          ))}
        </MainOptions>
        <ActionOptions>
          {actionOptions?.map((menu) => (
            <SidebarItem
              icon={menu.icon}
              title={menu.title}
              hasSubItems={!!menu.children?.length}
              hideArrow={!menu.children?.length}
              onClick={() => handleItemClick(menu)}
              collapsed={collapsed}
            >
              {menu.children?.map((subMenu) => (
                <SidebarItem title={subMenu.title} hideArrow onClick={() => handleItemClick(subMenu)} />
              ))}
            </SidebarItem>
          ))}
        </ActionOptions>
      </Content>
    </Container>
  )
}

export default memo(Sidebar)
